/**
 * Contact utility functions for contact list operations
 */

/**
 * Build a global map of normalized display names to arrays of userIds.
 * Used for disambiguation: if a name maps to >1 userId, those users need disambiguation.
 *
 * @param {Array} contactList - Array of contact objects with { id, name }
 * @param {string|null} currentUserName - Current user's display name (excluded from contactList)
 * @param {string|null} currentUserId - Current user's Matrix ID
 * @returns {Map<string, string[]>} Map of lowercased/trimmed names to arrays of userIds (only entries with >1 userId)
 */
export function buildGlobalDuplicatesMap(contactList, currentUserName, currentUserId) {
  const nameToUserIds = new Map();

  // Include current user in the map
  if (currentUserName && currentUserId) {
    const normalized = currentUserName.toLowerCase().trim();
    if (normalized) {
      nameToUserIds.set(normalized, [currentUserId]);
    }
  }

  if (Array.isArray(contactList)) {
    for (const contact of contactList) {
      const name = contact.name;
      if (!name) continue;
      const normalized = name.toLowerCase().trim();
      if (!normalized) continue;

      const existing = nameToUserIds.get(normalized);
      if (existing) {
        existing.push(contact.id);
      } else {
        nameToUserIds.set(normalized, [contact.id]);
      }
    }
  }

  // Remove entries with only 1 userId (no disambiguation needed)
  const keysToRemove = [];
  for (const [key, userIds] of nameToUserIds) {
    if (userIds.length <= 1) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach(key => nameToUserIds.delete(key));

  return nameToUserIds;
}

